import React, { JSX } from "react";
import { Box, Menu, MenuItem } from "@mui/material";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import Webcam from "react-webcam";
import CancelIcon from "@mui/icons-material/HighlightOff";
import Dialog from "../Dialog/Dialog";
import styles from "../../containers/Lab/Lab.module.css";
import dummy from "../../../assets/images/dummyProfile.png";
import ImageCrop from "../ImageCrop/ImageCrop";
import useLocalPhotoSave from "../../hooks/useLocalPhotoSave";
import { Client } from "../../interfaces/ClientInterface";
import { clientFullNameSelector } from "../../reducers/client";
import hasOwnProperty from "../../helpers/object";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";

const videoConstraints = {
  width: 1280,
  height: 720,
  facingMode: "user"
};

function ClientImageUploader({
  readOnly,
  action,
  initialImage,
  client
}: {
  readOnly?: boolean;
  action: (...args) => void;
  initialImage: string;
  client?: Client;
}): JSX.Element {
  const webcamRef = React.useRef(null);
  const { settings } = useCurrentResourceCentre();
  const disableWebcamMirroring = settings?.clientSettings.disableWebcamMirroring || false;
  const { savePhoto } = useLocalPhotoSave();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [cropperOpen, setCropperOpen] = React.useState(false);
  const [tmpImage, setTmpImage] = React.useState(initialImage);
  const [anchorEl, setAnchorEl] = React.useState(null);

  React.useEffect(() => {
    setTmpImage(initialImage || dummy);
  }, [initialImage]);

  const toggleDialog = () => {
    setDialogOpen((prevState) => !prevState);
  };

  const captureUserImage = React.useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc?.size >= 2097152) {
      toggleDialog();
    } else {
      setTmpImage(imageSrc);
      setCropperOpen(true);
    }
  }, [webcamRef]);

  const [openCamera, setOpenCamera] = React.useState(false);

  const handleOpenCamera = () => {
    setOpenCamera(true);
  };

  const handleCloseCamera = () => {
    setOpenCamera(false);
  };

  const handleImageClick = (event) => {
    if (!readOnly) {
      setAnchorEl(event.currentTarget);
    }
  };

  function handleImageChange(e) {
    e.preventDefault();
    if (e.target.files[0]?.size >= 2097152) {
      toggleDialog();
    } else {
      const file = e.target.files[0];
      if (!file) return;
      const nameOnly = file.name.lastIndexOf(".") + 1;
      const ext = file.name.substr(nameOnly, file.name.length).toLowerCase();
      if (["jpg", "jpeg", "png", "gif"].includes(ext) && file) {
        setTmpImage(URL.createObjectURL(file));
        setCropperOpen(true);
      } else {
        toggleDialog();
      }
    }
  }
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box display="flex">
      <Box
        component="img"
        src={tmpImage}
        alt="client-image"
        onClick={handleImageClick}
        sx={{
          height: "64px",
          width: "64px",
          borderRadius: "50%",
          border: "2px solid grey",
          cursor: `${readOnly ? "" : "pointer"}`
        }}
      />
      <div>
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem onClick={handleClose}>
            <label
              className={styles.userImageLabel}
              aria-label="Upload Image"
              htmlFor="upload-image"
            >
              Upload Image
              <input
                id="upload-image"
                type="file"
                onChange={handleImageChange}
                className={styles.imageInput}
                hidden
              />
            </label>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleOpenCamera();
              setAnchorEl(null);
            }}
          >
            Capture Image
          </MenuItem>
        </Menu>
      </div>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
        open={openCamera}
        onClose={handleCloseCamera}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={openCamera}>
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              border: "2px solid #000",
              boxShadow: theme.shadows[5],
              padding: theme.spacing(2, 4, 3)
            })}
          >
            <Webcam
              audio={false}
              height={720}
              mirrored={!disableWebcamMirroring}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width={1280}
              videoConstraints={videoConstraints}
            />

            <CancelIcon
              sx={{
                marginTop: "-32px",
                height: "20px",
                width: "20px",
                borderRadius: "50%",
                position: "absolute",

                left: "36px",
                backgroundColor: "#fff",
                cursor: "pointer"
              }}
              onClick={() => {
                handleCloseCamera();
              }}
            />

            <Box
              sx={{
                marginTop: "-32px",
                height: "50px",
                width: "50px",
                border: "5px #000",
                transform: "rotateY(180deg)",
                borderRadius: "50%",
                position: "absolute",
                marginBottom: "48px",
                bottom: 0,
                left: "50%",
                backgroundColor: "#fff",
                cursor: "pointer"
              }}
              onClick={() => {
                captureUserImage();
                handleCloseCamera();
              }}
            />
          </Box>
        </Fade>
      </Modal>

      {dialogOpen && (
        <Dialog
          title="Cannot upload image"
          description="File size limit is only 2MB or wrong file format"
          next={toggleDialog}
          readMode
        />
      )}
      <ImageCrop
        image={tmpImage}
        isOpen={cropperOpen}
        dynamicRatio={false}
        onCancel={() => {
          setCropperOpen(false);
          setTmpImage(initialImage);
        }}
        onSave={async (croppedImage) => {
          action(croppedImage);
          setTmpImage(croppedImage);
          setCropperOpen(false);
          if (client && hasOwnProperty(client, "firstName")) {
            await savePhoto(croppedImage, {
              clientName: clientFullNameSelector(client),
              clientId: client?.id
            });
          }
        }}
      />
    </Box>
  );
}

export default ClientImageUploader;
